import type { AxiosError } from "axios";

type ErrorResponseData =
  | {
      message?: string;
    }
  | string
  | null
  | undefined;

/**
 * Extract a user-friendly error message from an unknown error value.
 * Safely handles Axios errors and falls back to a generic message.
 */
export const getErrorMessage = (
  error: unknown,
  fallbackMessage: string = "Failed to send document. Please try again."
): string => {
  const maybeAxiosError = (error ?? {}) as Partial<
    AxiosError<ErrorResponseData>
  >;
  const response = maybeAxiosError.response;

  if (response) {
    // Try to use the backend's specific error message first
    const data = response.data;
    if (typeof data === "object" && data !== null && "message" in data) {
      const maybeMessage = (data as { message?: string }).message;
      if (maybeMessage) return maybeMessage;
    }
    if (typeof data === "string" && data.trim().length > 0) {
      return data;
    }

    // Fall back to generic status-based messages
    switch (response.status) {
      case 400:
        return "Missing required information. Please check the project ID and project email.";
      case 404:
        return "Project not found. Please verify the project exists.";
      case 500:
        return "Server error. Please try again later or contact support.";
      default:
        break;
    }
  }

  return fallbackMessage;
};

export default getErrorMessage;
